import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { compareDesc } from 'date-fns';

import { instance } from '.';
import { userInfoAtom } from './member';
import { selectedDeviceAtom } from '@/components/common/MapView';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';

import { isAxiosError, type AxiosError } from 'axios';
import type { Device, DeviceData, DevicePatchProps, Period } from '@/@types/device';
import type { APIResponse, APIResponseWithPagination } from '@/@types/types';

const DEVICE_QUERY_NAME = 'DEVICES_';

export const devicesAtom = atomWithQuery<Array<Device>, AxiosError>((get) => ({
  queryKey: [DEVICE_QUERY_NAME + 'LIST'],
  queryFn: async ({ queryKey: [,] }) => {
    const { data: userInfo } = get(userInfoAtom);
    const res = await getDevices(userInfo.site[0].id);
    return res;
  },
}));

export const getDevices = async (siteId: string) => {
  const { data } = await instance.get<APIResponse<Device[]>>('/device/sensors/site/' + siteId);
  return data.data;
};

export const deviceDataAtom = atomWithQuery<DeviceData[], Error, DeviceData[], [string, Device, Period]>((get) => ({
  queryKey: ['DEVICE_DATA', get(selectedDeviceAtom), get(deviceDataPeriodAtom)],
  queryFn: async ({ queryKey: [, device, period] }) => {
    if (!device) return [];
    const { data: userInfo } = get(userInfoAtom);
    const data = await getDeviceData(device.id, userInfo.site[0].id, period);

    return data;
  },
}));
async function getDeviceData(sensorId: string, siteId: string, period: Period): Promise<DeviceData[]> {
  try {
    const { data } = await instance.get<APIResponseWithPagination<DeviceData[]>>(`/device/sensor/${sensorId}/data`, {
      params: { siteId, ...period },
    });
    data.data.data.sort((a, b) => compareDesc(a.time, b.time));
    return data.data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getDeviceData] API error code:', err.response.data.errorCode);
      return [];
    }
    console.error('[getDeviceData] Unknown error', err);
    return [];
  }
}

export const patchDeviceNameAtom = atomWithMutation(() => ({
  mutationKey: ['DEVICE_DATA'],
  mutationFn: async (body: DevicePatchProps) => {
    return patchDeviceName(body);
  },
}));

const patchDeviceName = async (body: DevicePatchProps) => {
  return await instance.patch(`/device/sensor/${body.id}/name`, { name: body.name });
};

type PatchDeviceGroupParams = { sensorId: string; groupId: string };
export const patchDeviceGroupAtom = atomWithMutation(() => ({
  mutationFn: async ({ sensorId, groupId }: PatchDeviceGroupParams) => {
    const isSuccess = await patchDeivceGroup(sensorId, groupId);
    return isSuccess;
  },
}));
async function patchDeivceGroup(sensorId: string, groupId: string) {
  try {
    const { data } = await instance.patch<APIResponse>(`/device/sensor/${sensorId}/group`, { groupId });
    if (data.errorCode !== 'OK') {
      console.error('[patchDeivceGroup] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[patchDeivceGroup] API error code:', err.response.data.errorCode);
    } else {
      console.error('[patchDeivceGroup] Unknown error', err);
    }

    return false;
  }
}
